import { useCallback, useEffect, useRef, useState } from 'react';
import * as API from '../../../../api/produto';
import { getEmpresaScope } from '../../../../helpers/sessionEmpresa';
import { sugerirMapas } from './mapeamentoVinculos';

export default function useDuplicidadesModule({ empresaInicial, selecaoInicial, onConcluida } = {}) {
    const [empresaId] = useState(() => {
        const id = Number(empresaInicial ?? getEmpresaScope().empresaId);
        return Number.isSafeInteger(id) && id > 0 ? id : '';
    });
    const [pesquisa, setPesquisa] = useState('');
    const [criterios, setCriterios] = useState(['DESCRICAO_NORMALIZADA', 'CODIGO_COMPARTILHADO', 'FORNECEDOR_CPROD', 'VINCULO_REPETIDO']);
    const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 25 });
    const [lista, setLista] = useState(null);
    const [comparacao, setComparacao] = useState(null);
    const [principalId, setPrincipalId] = useState('');
    const [principalEmbalagemId, setPrincipalEmbalagemId] = useState('');
    const [mapas, setMapas] = useState({});
    const [justificativa, setJustificativa] = useState('');
    const [previa, setPrevia] = useState(null);
    const [resultado, setResultado] = useState(null);
    const [auditoria, setAuditoria] = useState(null);
    const chaveConfirmacao = useRef(null);
    const travaConfirmacao = useRef(false);
    const [ocupado, setOcupado] = useState(false);
    const [gravando, setGravando] = useState(false);
    const [erro, setErro] = useState('');
    const versaoRequisicao = useRef(0);
    const montado = useRef(true);
    useEffect(() => { montado.current = true; return () => { montado.current = false; versaoRequisicao.current++; }; }, []);
    const invalidar = useCallback(() => { versaoRequisicao.current++; setPrevia(null); setOcupado(false); }, []);
    const executar = useCallback(async (fn, sucesso) => {
        if (!empresaId) { setErro('Empresa do acesso não identificada. Entre novamente no sistema.'); return; }
        const versao = ++versaoRequisicao.current;
        setOcupado(true); setErro('');
        try {
            const data = await fn();
            if (montado.current && versao === versaoRequisicao.current) sucesso(data);
        } catch (e) {
            if (montado.current && versao === versaoRequisicao.current) setErro(e.response?.data?.message || e.message || 'Falha na análise.');
        } finally {
            if (montado.current && versao === versaoRequisicao.current) setOcupado(false);
        }
    }, [empresaId]);
    const limparComparacao = () => { setComparacao(null); setPrevia(null); setPrincipalId(''); setPrincipalEmbalagemId(''); setMapas({}); setJustificativa(''); };
    const buscar = (page = { pageIndex: 0, pageSize: pagination.pageSize }) => {
        limparComparacao(); setPagination(page);
        return executar(() => API.identificarDuplicidades({ empresaId: Number(empresaId), pesquisa, criterios, start: page.pageIndex * page.pageSize, size: page.pageSize }), setLista);
    };
    const comparar = (produtoIds) => {
        limparComparacao();
        return executar(() => API.compararDuplicidades({ empresaId: Number(empresaId), produtoIds }), setComparacao);
    };
    const escolherPrincipal = (value) => {
        invalidar(); setPrincipalId(Number(value));
        const principais = (comparacao?.apresentacoes || []).filter((v) => Number(v.produtoId) === Number(value) && Number(v.principal) === 1 && Number(v.ativo) === 1 && !v.deletedAt);
        setPrincipalEmbalagemId(principais.length === 1 ? principais[0].id : '');
        setMapas(sugerirMapas(comparacao, value));
    };
    const alterarMapa = (id, value) => {
        invalidar();
        setMapas((old) => ({ ...old, [id]: value === 'preservar' ? { acao: 'PRESERVAR_VINCULO' } : { acao: 'ASSOCIAR_EQUIVALENTE', produtoEmbalagemDestinoId: Number(value) } }));
    };
    const gerarPrevia = () => {
        setPrevia(null); chaveConfirmacao.current = null;
        const pedido = {
            versaoContrato: comparacao.versaoContrato, empresaId: Number(empresaId), produtoPrincipalId: Number(principalId),
            produtoOrigemIds: comparacao.produtos.map((p) => Number(p.id)).filter((id) => id !== Number(principalId)),
            produtoEmbalagemPrincipalId: principalEmbalagemId ? Number(principalEmbalagemId) : null,
            justificativa,
            mapeamentoVinculos: Object.entries(mapas).map(([id, value]) => ({ produtoEmbalagemOrigemId: Number(id), ...value })),
        };
        return executar(() => API.gerarPreviaMesclagem(pedido), (data) => setPrevia({ ...data, pedido }));
    };
    const confirmarMesclagem = () => {
        if (travaConfirmacao.current || !previa?.aptaParaMesclagem || !previa.confirmacaoToken) return;
        if (!chaveConfirmacao.current) {
            const bytes = crypto.getRandomValues(new Uint8Array(16));
            bytes[6] = (bytes[6] & 15) | 64; bytes[8] = (bytes[8] & 63) | 128;
            const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
            chaveConfirmacao.current = `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
        }
        travaConfirmacao.current = true;
        setGravando(true);
        return executar(() => API.executarMesclagem({ previa: previa.pedido, previaHash: previa.previaHash,
            confirmacaoToken: previa.confirmacaoToken, chaveIdempotencia: chaveConfirmacao.current, cienteCustoDerivado: true }),
        (data) => { setResultado(data); setAuditoria(null); setLista(null); limparComparacao(); onConcluida?.(data); }).finally(() => { travaConfirmacao.current = false; if (montado.current) setGravando(false); });
    };
    const consultarAuditoria = (identificador) => executar(() => API.consultarMesclagem({ empresaId: Number(empresaId), ...identificador }), setAuditoria);
    useEffect(() => {
        if (selecaoInicial?.length >= 2) comparar(selecaoInicial);
        // A janela é remontada para cada seleção; não altera empresa durante a análise.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    return { empresaId, pesquisa, setPesquisa: (v) => { invalidar(); setPesquisa(v); setLista(null); limparComparacao(); },
        criterios, setCriterios: (v) => { invalidar(); setCriterios(v); setLista(null); limparComparacao(); }, pagination, lista, buscar, comparar,
        comparacao, principalId, escolherPrincipal, principalEmbalagemId,
        escolherEmbalagem: (v) => { invalidar(); setPrincipalEmbalagemId(v); }, mapas, alterarMapa,
        justificativa, setJustificativa: (v) => { invalidar(); setJustificativa(v); }, previa, gerarPrevia, resultado, auditoria, confirmarMesclagem, consultarAuditoria, ocupado, gravando, erro };
}
